import { useEffect, useRef, useState } from "react";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import { useShowAlert } from "../../../context/AlertContext";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { CheckIcon, MoveVerticalIcon, RemoveIcon, SearchIcon, XIcon } from "../../../constants/Icons";

const FavouriteProduct = ({
  onClose,
  favouriteProductsData,
  initialFavourites = [],
}) => {
  const [favouriteProducts, setFavouriteProducts] = useState(initialFavourites);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const showAlert = useShowAlert();

  const suggestionsRef = useRef(null);
  const suggestionRefs = useRef([]);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [isPreLoading, setPreLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const debounceTimerRef = useRef(null);
  const controllerRef = useRef(null);
  const requestIdRef = useRef(0);

  const stopEventPropagation = (e) => e.stopPropagation();

  useEffect(() => {
    if (Array.isArray(initialFavourites)) {
      const normalized = initialFavourites.map((item) => ({
        ...item,
        favouriteId: item._id,
        productId: item.productId,
      }));
      setFavouriteProducts(normalized);
    }
  }, []); // ✅ only on first load

  const handleProductSearch = (term) => {
    setProductSearchTerm(term);
    if (term.trim() === "") {
      setProductSuggestions([]);
      return;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    debounceTimerRef.current = setTimeout(async () => {
      try {
        setPreLoading(true);

        controllerRef.current = new AbortController();
        const currentId = ++requestIdRef.current;

        const response = await AxiosInstance.get(
          `module/product/search-hybrid-active?searchtxt=${term}`,
          { signal: controllerRef.current.signal }
        );
        if (currentId === requestIdRef.current) {
          setProductSuggestions(response.data.results || []);
        }
      } catch (error) {
        if (error.name !== "CanceledError" && error.name !== "AbortError") {
          console.error("API Error:", error);
        }
      } finally {
        setPreLoading(false);
      }
    }, 200);
  };

  const handleSelectProduct = async (product) => {
    setProductSearchTerm("");
    setShowProductDropdown(false);
    setHighlightedIndex(0);

    try {
      // ✅ Step 1: prevent duplicate adds
      const alreadyExists = favouriteProducts.some(
        (p) => p.productId === product._id
      );
      if (alreadyExists) {
        showAlert("warning", "This product is already in your favourites!");
        return;
      }

      // ✅ Step 2: Add new favourite
      const favResponse = await AxiosInstance.post("module/product/favourite", {
        productid: product._id,
      });

      if (
        favResponse?.status === 201 ||
        favResponse?.data?.statusCode === 201
      ) {
        const newFavourite = favResponse.data.results; // { _id, productId, ... }

        // ✅ Step 3: If this is the FIRST product, fetch the whole list
        if (favouriteProducts.length === 0) {
          try {
            const listResponse = await AxiosInstance.get(
              "module/product/favourite"
            );
            const results = listResponse?.data?.results || [];

            // 👉 Normalize each result with favouriteId
            const mapped = results.map((item) => ({
              ...item,
              favouriteId: item._id,
            }));

            setFavouriteProducts(mapped);
            showAlert("success", "Product added to favourites!");
            return; // ✅ Stop here — list replaces local state fully
          } catch (listErr) {
            console.error("Error fetching favourites list:", listErr);
          }
        }

        // ✅ Step 4: Fetch full product details for new favourite
        const viewResponse = await AxiosInstance.get(
          `module/product/view-product/${newFavourite.productId}`
        );
        const productDetails = viewResponse?.data?.results;

        // ✅ Step 3: Combine both favouriteId and product details correctly
        const productWithFavouriteId = {
          ...productDetails,
          favouriteId: newFavourite._id, // ✅ favourite record id
          productId: newFavourite.productId, // ✅ actual product id
        };
        // ✅ Step 4: Update UI
        quickProduct(productWithFavouriteId);
        showAlert("success", "Product added to favourites!");
      }
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  // ✅ Add/Replace selected product
  const quickProduct = (productDetails) => {
    if (!productDetails) return;

    setFavouriteProducts((prev) => {
      const updated = [...prev];

      if (selectedIndex !== null) {
        updated[selectedIndex] = productDetails;
      } else {
        // ✅ Avoid duplicates based on favouriteId
        const exists = updated.some(
          (p) => p.favouriteId === productDetails.favouriteId
        );

        if (!exists) updated.push(productDetails);
      }

      return updated;
    });

    setSelectedIndex(null);
  };

  const handleRemoveProduct = async (index) => {
    try {
      const productToRemove = favouriteProducts[index];
      if (!productToRemove?._id) return;
      const response = await AxiosInstance.delete(
        `module/product/favourite/${productToRemove.favouriteId}`
      );
      if (response?.status === 200) {
        setFavouriteProducts((prev) => prev.filter((_, i) => i !== index));
        showAlert("success", "Product removed from favourites.");
      } else {
        showAlert("warning", "Failed to remove favourite product.");
      }
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const handleProductKeyDown = (e) => {
    if (["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) {
      e.stopPropagation();
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        Math.min(prev + 1, productSuggestions.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0) {
        const selected = productSuggestions[highlightedIndex];
        handleSelectProduct(selected);
        setShowProductDropdown(false);
        setProductSearchTerm("");
        setHighlightedIndex(-1);
      }
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && suggestionRefs.current[highlightedIndex]) {
      suggestionRefs.current[highlightedIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  // ✅ ESC to close
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  // ✅ Notify parent if needed
  useEffect(() => {
    if (typeof favouriteProductsData === "function") {
      favouriteProductsData(favouriteProducts);
    }
  }, [favouriteProducts, favouriteProductsData]);

  return (
    <div>
      <div className="modal-backdrop show" onClick={onClose}></div>

      <div
        className="modal show"
        id="FavouriteProductModal"
        tabIndex="-1"
        aria-modal="true"
        role="dialog"
        onClick={onClose}
        style={{ display: "block" }}
      >
        <div
          className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
          onClick={stopEventPropagation}
        >
          <div
            className="modal-content d-flex flex-column"
            style={{ height: "100%" }}
          >
            <div className="modal-header">
              <h6 className="modal-title">Manage Favourite Products</h6>
              <div className="d-flex align-items-center fw-bold text-dark mb-0 ms-auto me-2">
                <button
                  type="button"
                  className="btn-close"
                  onClick={onClose}
                ></button>
              </div>
            </div>

            <div
              className="modal-body bg-light flex-grow-1"
              style={{ minHeight: "400px" }}
            >
              <div className="card mb-0">
                <div className="card-body">
                  <div className="row mb-0">
                    <div className="row  align-items-center px-4">
                      Select Product
                      <div
                        className="col-lg-6"
                        style={{ position: "relative" }}
                      >
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            name="search"
                            ref={suggestionsRef}
                            value={productSearchTerm}
                            autoComplete="off"
                            onChange={(e) => {
                              handleProductSearch(e.target.value);
                              setShowProductDropdown(true);
                              setHighlightedIndex(0);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.stopPropagation(); // ✅ prevents bubbling to parent modals

                                if (
                                  highlightedIndex >= 0 &&
                                  productSuggestions[highlightedIndex]
                                ) {
                                  handleSelectProduct(
                                    productSuggestions[highlightedIndex]
                                  );
                                } else if (productSuggestions.length === 1) {
                                  handleSelectProduct(productSuggestions[0]);
                                }
                              } else {
                                handleProductKeyDown(e);
                              }
                            }}
                            onFocus={() => {
                              if (productSuggestions.length > 0) {
                                setShowProductDropdown(true);
                              }
                            }}
                            placeholder="Search products by hsnsac, title..."
                            autoFocus
                          />
                          <button className="input-group-text">
                            <SearchIcon/>
                          </button>
                        </div>
                        {productSearchTerm &&
                          productSuggestions.length === 0 && (
                            <ul className="suggestions-list">
                              <li className="suggestion-item text-muted">
                                No products data was found
                              </li>
                            </ul>
                          )}
                        {showProductDropdown &&
                          productSuggestions.length > 0 && (
                            <ul className="suggestions-list">
                              {productSuggestions.map((product, index) => (
                                <li
                                  ref={(el) =>
                                    (suggestionRefs.current[index] = el)
                                  }
                                  key={product._id}
                                  onClick={() =>
                                    handleSelectProduct(product, true)
                                  }
                                  className={`suggestion-item ${
                                    highlightedIndex === index
                                      ? "highlighted"
                                      : ""
                                  }`}
                                >
                                  <span className="badge rounded-pill bg-label-navy">
                                    {product.code}
                                  </span>{" "}
                                  - {product.title} -{" "}
                                  <span className="badge bg-label-fall rounded-pill">
                                    ₹
                                    {CurrencyConverter.toCurrency(
                                      product.saleprice || 0,
                                      "INR"
                                    )}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                      </div>
                    </div>
                  </div>
                  <DragDropContext
                    onDragStart={() => {
                      document.body.style.overflow = "hidden";
                    }}
                    onDragEnd={async (result) => {
                      document.body.style.overflow = "auto";
                      if (!result.destination) return;

                      // ✅ Step 1: reorder list visually
                      const reordered = Array.from(favouriteProducts);
                      const [moved] = reordered.splice(result.source.index, 1);
                      reordered.splice(result.destination.index, 0, moved);
                      setFavouriteProducts(reordered);

                      const fromFavourite =
                        favouriteProducts[result.source.index];
                      if (!fromFavourite?.favouriteId) return;

                      // ✅ Step 2: determine visible (non-hidden) products
                      const visibleProducts = favouriteProducts.filter(
                        (p) => p.productId !== null
                      );

                      // ✅ Step 3: detect if moving up or down
                      const movingDown =
                        result.destination.index > result.source.index;

                      let toFavourite = null;

                      if (movingDown) {
                        // 🔽 Moving downwards
                        if (
                          result.destination.index >=
                          favouriteProducts.length - 1
                        ) {
                          // → moved to bottom, target is last visible product
                          toFavourite =
                            visibleProducts[visibleProducts.length - 1];
                        } else {
                          // → normal downward move, attach to the product above destination
                          toFavourite =
                            favouriteProducts[result.destination.index];
                        }
                      } else {
                        // 🔼 Moving upwards
                        if (result.destination.index === 0) {
                          // → moved to top, target is hidden product (anchor)
                          toFavourite = favouriteProducts.find(
                            (p) => p.productId === null
                          );
                        } else {
                          // → normal upward move, target is product just above
                          toFavourite =
                            favouriteProducts[result.destination.index - 1];
                        }
                      }

                      if (!toFavourite?.favouriteId) return;

                      // ✅ Step 4: call API
                      try {
                        const response = await AxiosInstance.patch(
                          `module/product/favourite/rearrange?fromFavouriteId=${fromFavourite.favouriteId}&toFavouriteId=${toFavourite.favouriteId}`
                        );

                        if (
                          response?.status === 200 ||
                          response?.data?.statusCode === 200
                        ) {
                          // showAlert("success", "Favourites rearranged successfully!");
                        }
                      } catch (error) {
                        handleError(error, showAlert);
                      }
                    }}
                  >
                    <div className="table-responsive mt-3">
                      <table className="table align-middle mb-0">
                        <thead className="table-light text-center">
                          <tr>
                            <th style={{ width: "40px" }}>⇅</th>
                            <th>Barcode</th>
                            <th>Title</th>
                            <th>MRP</th>
                            <th>Discount</th>
                            <th>Sale Price</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <Droppable
                          droppableId="favourites"
                          direction="vertical"
                        >
                          {(provided) => (
                            <tbody
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              style={{
                                userSelect: "none",
                                paddingBottom: "20px",
                              }}
                            >
                              {favouriteProducts?.length > 0 ? (
                                favouriteProducts.map((item, index) => (
                                  <Draggable
                                    key={item.favouriteId || index}
                                    draggableId={(
                                      item.favouriteId || index
                                    ).toString()}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <tr
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        key={item.favouriteId || index}
                                        // 👇 Hide row visually but keep in DOM for DnD
                                        style={{
                                          display:
                                            item.productId === null
                                              ? "none"
                                              : "table-row",
                                          opacity: snapshot.isDragging
                                            ? 0.7
                                            : 1,
                                          ...provided.draggableProps.style,
                                        }}
                                        className={`cursor-pointer hover-row`}
                                      >
                                        <td className="text-center">
                                          <MoveVerticalIcon iClass={"fs-5 text-muted"}/>
                                        </td>
                                        <td className="text-center">
                                          <span className="badge rounded-pill bg-label-navy">
                                            {item.code || "-"}
                                          </span>
                                        </td>
                                        <td className="text-center">
                                          <span className="text-heading fw-medium text-wrap">
                                            {item.title || "-"}
                                          </span>
                                        </td>
                                        <td className="text-center">
                                          ₹
                                          {CurrencyConverter.toCurrency(
                                            item.mrp || 0,
                                            "INR"
                                          )}
                                        </td>
                                        <td className="text-center">
                                          ₹
                                          {CurrencyConverter.toCurrency(
                                            item.discount || 0,
                                            "INR"
                                          )}
                                        </td>
                                        <td className="text-center fw-medium">
                                          ₹
                                          {CurrencyConverter.toCurrency(
                                            item.saleprice || 0,
                                            "INR"
                                          )}
                                        </td>

                                        <td className="text-center">
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleRemoveProduct(index)
                                            }
                                            className="btn p-0 border-0 bg-transparent text-danger"
                                            title="Remove Product"
                                          >
                                            <RemoveIcon iClass={"fs-16"}/>
                                          </button>
                                        </td>
                                      </tr>
                                    )}
                                  </Draggable>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={"7"} className="text-center p-3">
                                    <p className="text-muted mb-3">
                                      No products yet added select the product
                                      from search
                                    </p>
                                  </td>
                                </tr>
                              )}
                              {provided.placeholder}
                            </tbody>
                          )}
                        </Droppable>
                      </table>
                    </div>
                  </DragDropContext>
                </div>
              </div>
            </div>

            <div className="modal-footer d-block">
              <button
                type="button"
                className="btn text-left btn-outline-secondary"
                onClick={onClose}
              >
                <XIcon/> Cancel
              </button>
              <button
                type="button"
                className={`btn text-right me-2 bg-label-primary`}
                onClick={onClose}
              >
                <>
                  <CheckIcon iClass={"me-1 fs-6"}/> Done
                </>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FavouriteProduct;
